import { request } from "$/lib/api"
import {
  CreateServiceFee,
  Route,
  ServiceFee,
  UpdateServiceFee,
  UpdateStandardFee,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

interface FeeFilters {
  includedIds?: string[]
  storeId: string
}

async function fetchFees(filters: FeeFilters): Promise<ServiceFee[]> {
  const { storeId, includedIds } = filters

  return await request<ServiceFee[]>({
    url: populateRoute(
      Route.SERVICE_JOB_FEE_CONTROLLER__FIND_MANY_BY_STORE_ID,
      storeId,
    ),
    params: {
      includedIds,
    },
    method: "GET",
  })
}

async function createFee(data: CreateServiceFee, storeId: string) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_FEE_CONTROLLER__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function deleteFee(id: string) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_FEE_CONTROLLER__DELETE, id),
    method: "DELETE",
  })
}

async function updateFee(data: UpdateServiceFee, id: string) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_FEE_CONTROLLER__UPDATE, id),
    method: "PATCH",
    data,
  })
}

async function updateStandardFee(data: UpdateStandardFee, id: string) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_FEE_CONTROLLER__UPDATE_STANDARD, id),
    method: "PATCH",
    data,
  })
}

export function useFeeMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () =>
    await queryClient.invalidateQueries(feeQueries.list({ storeId }))

  const createMutation = useMutation({
    mutationFn: (data: CreateServiceFee) => createFee(data, storeId),
    onSettled: invalidateQueries,
  })

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => {
      const promises = ids.map((id) => deleteFee(id))
      return Promise.all(promises)
    },
    onSettled: invalidateQueries,
  })

  const updateMutation = useMutation({
    mutationFn: ({ data, id }: { data: UpdateServiceFee; id: string }) =>
      updateFee(data, id),
    onSettled: invalidateQueries,
  })

  const updateStandardFeeMutation = useMutation({
    mutationFn: ({ data, id }: { data: UpdateStandardFee; id: string }) =>
      updateStandardFee(data, id),
    onSettled: invalidateQueries,
  })

  return {
    createMutation,
    deleteMutation,
    updateMutation,
    updateStandardFeeMutation,
  }
}

const feeQueries = {
  all: () => ["fee"],
  lists: () => [...feeQueries.all(), "list"],
  list: (filters: FeeFilters) =>
    queryOptions({
      queryKey: [...feeQueries.lists(), filters],
      queryFn: () => fetchFees(filters),
    }),
}

export default feeQueries
